import React from 'react';

import {
  Calendar,
  CalendarCell,
  DatePicker,
  DatePickerChangeEvent,
} from '@progress/kendo-react-dateinputs';
import { FieldRenderProps, FieldWrapper } from '@progress/kendo-react-form';
import { Error, Hint, Label } from '@progress/kendo-react-labels';

const customCell = (props) => {
  return <CalendarCell {...props} isDisabled={!props.isInRange || props.isWeekend} />;
};

const WeekdayCalender = (props) => {
  return <Calendar {...props} cell={customCell} />;
};

export const SbcoDatePicker = React.memo(function SbcoDatePicker(
  fieldRenderProps: FieldRenderProps
) {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    hintDirection,
    optional,
    weekdayonly,
    onChange,
    min,
    max,
    minMaxErrorPrefix,
    ...others
  } = fieldRenderProps;

  const showValidationMessage: string | false | null = touched && validationMessage;
  const showHint: boolean = !showValidationMessage && hint;
  const hintId: string = showHint ? `${id}_hint` : '';
  const errorId: string = showValidationMessage ? `${id}_error` : '';
  const labelId: string = label ? `${id}_label` : '';

  // Helper to check if a date is a weekday
  const isWeekday = (date: Date | null) => {
    if (!date) return true;
    const day = date.getDay();
    return day !== 0 && day !== 6;
  };
  const isWithinRange = (date: Date | null) => {
    if (!date) return true;
    if (!min && !max) return true;

    return !((min && date < new Date(min)) || (max && date > new Date(max)));
  };
  const handleChange = (e: DatePickerChangeEvent) => {
    const date = e.value;
    if (weekdayonly && !isWeekday(date)) {
      // Show basic alert instead of notification
      alert('Please select a weekday.');
      onChange?.({ value: null });
      return;
    }
    let dateMessagePart = '';

    if (min && !max) {
      dateMessagePart = `after ${min?.toLocaleDateString()}`;
    } else if (max && !min) {
      dateMessagePart = `before ${max?.toLocaleDateString()}`;
    } else if (min && max) {
      dateMessagePart = `between ${min?.toLocaleDateString()} and ${max?.toLocaleDateString()}`;
    }

    if (!isWithinRange(date)) {
      // Show basic alert instead of notification
      alert(`${minMaxErrorPrefix ?? ''}Please select a date ${dateMessagePart}.`);
      onChange?.({ value: null });
      return;
    }

    onChange?.(e);
  };

  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
        optional={optional}
        className="k-form-label"
      >
        {label}
      </Label>
      <div className="k-form-field-wrap">
        <DatePicker
          aria-labelledby={labelId}
          aria-describedby={`${hintId} ${errorId}`}
          valid={valid}
          id={id}
          placeholder="mm/dd/yyyy"
          calendar={weekdayonly ? WeekdayCalender : Calendar}
          disabled={disabled}
          className="sbco-datepicker"
          onChange={handleChange}
          {...others}
        />
        {showHint && (
          <Hint id={hintId} direction={hintDirection}>
            {hint}
          </Hint>
        )}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </div>
    </FieldWrapper>
  );
});
